/**
 * Live call-flow telemetry — the client half of the voice/video call
 * observability pipeline.
 *
 * Buffers structured, correlated call + audio-session events and SHIPS them
 * (batched, resilient, retrying, flushed on visibilitychange/pagehide) to the
 * authenticated `recordCallTelemetry` AppSync mutation, so any device / user /
 * call instance can be inspected after the fact — especially the persistent iOS
 * locked-screen CallKit-answer silent-audio bug, where the WebView is
 * backgrounded/suspended and console logs are invisible.
 *
 * Every event is keyed by:
 *   { ts, bootId, callId, userId, deviceId, platform, event, data, seq }
 *   - bootId    : minted once per page load / process boot (a WKWebView reload
 *                 mints a new one — that discontinuity is itself a signal).
 *   - deviceId  : stable per-install id (Capacitor Preferences), see device-id.ts
 *   - userId    : the current authenticated user (set via setCallFlowUser).
 *   - callId    : the active call (set via setCallFlowCall) or a per-event override.
 *
 * Native iOS/Android layers emit their signals (AVAudioSession / CallKit /
 * AudioManager / Telecom / lifecycle / WKWebView terminate) as a `callTelemetry`
 * Capacitor event which this module funnels into the SAME buffer — so
 * everything flows through ONE shipping path.
 *
 * Design rules (telemetry must never break or slow a real call):
 *   - Fire-and-forget and fully guarded — recording and shipping never throw
 *     into the call path.
 *   - Cheap: recording is a push onto a bounded queue.
 *   - Kill switch: localStorage 'ascend-disable-call-telemetry' = '1' stops
 *     shipping.
 */
import { whenAuthStorageReady } from '../config/amplify'
import { getNativePlatform } from '../utils/platform'
import { getCachedDeviceId, getDeviceId, primeDeviceId } from '../utils/device-id'
import {
  primeCallTelemetryAuth,
  shipCallTelemetry,
  shipCallTelemetryOnUnload,
} from '../services/voice-call/call-telemetry-service'

export type CallTelemetryEvent = {
  /** Epoch milliseconds. */
  ts: number
  bootId: string
  callId: string | null
  userId: string | null
  deviceId: string
  platform: 'ios' | 'android' | 'web'
  /** Stable label, e.g. 'join:start', 'chime:audioVideoDidStart', 'native:ios:routeChange'. */
  event: string
  data?: Record<string, unknown>
  /** Monotonic per-boot sequence for stable ordering when ts collides. */
  seq: number
}

const MAX_QUEUE = 400
const BATCH_MAX = 40
const FLUSH_INTERVAL_MS = 3_000
const RETRY_BACKOFF_MS = 8_000
const DISABLE_KEY = 'ascend-disable-call-telemetry'

const bootId = mintBootId()
const platform = getNativePlatform()

let seqCounter = 0
let currentUserId: string | null = null
let currentCallId: string | null = null
let deviceId: string = getCachedDeviceId() ?? getDeviceId()
/** `version/build` (CFBundleVersion / versionCode). Stamped onto every event's data. */
let appBuild: string | null = null

/** Outbound queue awaiting shipment. */
const queue: CallTelemetryEvent[] = []

let flushTimer: ReturnType<typeof setTimeout> | null = null
let flushing = false
let initialised = false

function mintBootId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    /* fall through */
  }
  return `boot-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

function shippingDisabled(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(DISABLE_KEY) === '1'
  } catch {
    return false
  }
}

/** Set the current authenticated user id so subsequent events are attributed. */
export function setCallFlowUser(userId: string | null): void {
  currentUserId = userId || null
}

/** Set (or clear) the active call id that subsequent events belong to. */
export function setCallFlowCall(callId: string | null): void {
  currentCallId = callId || null
}

/**
 * Record a call-flow event. Cheap, guarded, fire-and-forget. `opts.callId`
 * overrides the ambient current call id for this one event.
 */
export function recordCallFlow(
  event: string,
  data?: Record<string, unknown>,
  opts?: { callId?: string | null },
): void {
  try {
    const stamped =
      appBuild || (data && Object.keys(data).length)
        ? { ...(appBuild ? { appBuild } : {}), ...(data ?? {}) }
        : undefined
    const entry: CallTelemetryEvent = {
      ts: Date.now(),
      bootId,
      callId: opts?.callId !== undefined ? opts.callId : currentCallId,
      userId: currentUserId,
      deviceId: deviceId || getCachedDeviceId() || 'unknown',
      platform,
      event,
      ...(stamped && Object.keys(stamped).length ? { data: stamped } : {}),
      seq: seqCounter++,
    }

    queue.push(entry)
    if (queue.length > MAX_QUEUE) queue.splice(0, queue.length - MAX_QUEUE)

    if (!initialised) initCallFlowLog()
    if (queue.length >= BATCH_MAX) scheduleFlush(0)
    else scheduleFlush(FLUSH_INTERVAL_MS)
  } catch {
    /* telemetry must never throw into the call path */
  }
}

function scheduleFlush(delay: number): void {
  if (flushTimer || flushing) return
  flushTimer = setTimeout(() => {
    flushTimer = null
    void flush()
  }, delay)
}

async function flush(): Promise<void> {
  if (flushing || queue.length === 0) return
  if (shippingDisabled()) return
  flushing = true
  const batch = queue.splice(0, BATCH_MAX)
  let ok = false
  try {
    ok = await shipCallTelemetry(batch)
  } catch {
    ok = false
  }
  flushing = false
  if (!ok) {
    // Re-queue the batch at the front (bounded) and back off.
    queue.unshift(...batch)
    if (queue.length > MAX_QUEUE) queue.splice(0, queue.length - MAX_QUEUE)
    scheduleFlush(RETRY_BACKOFF_MS)
  } else if (queue.length > 0) {
    scheduleFlush(FLUSH_INTERVAL_MS)
  }
}

/** Best-effort flush that survives page teardown (keepalive). */
function flushOnUnload(): void {
  if (shippingDisabled() || queue.length === 0) return
  const batch = queue.splice(0, MAX_QUEUE)
  shipCallTelemetryOnUnload(batch)
}

/**
 * Map a native `callTelemetry` payload into the buffer. Native namespaces its
 * own event labels (e.g. 'ios:audioSession', 'android:telecom'); we prefix them
 * with 'native:' and carry callId out of the payload if present.
 */
function ingestNativeTelemetry(payload: Record<string, unknown>): void {
  const rawEvent = typeof payload.event === 'string' ? payload.event : 'unknown'
  const callId = typeof payload.callId === 'string' ? payload.callId : undefined
  const { event: _e, callId: _c, ...rest } = payload
  recordCallFlow(`native:${rawEvent}`, rest as Record<string, unknown>, {
    callId: callId ?? undefined,
  })
}

/**
 * Initialise the telemetry pipeline: prime the device id + auth token, wire the
 * native bridge, and register lifecycle flush handlers. Idempotent and safe to
 * call from module import or app bootstrap.
 */
function primeAppBuild(): void {
  void import('@capacitor/app')
    .then(({ App }) => App.getInfo())
    .then((info) => {
      const version = typeof info.version === 'string' ? info.version.trim() : ''
      const build = typeof info.build === 'string' ? info.build.trim() : ''
      appBuild = [version, build].filter(Boolean).join('/') || null
    })
    .catch(() => {
      if (!appBuild) appBuild = platform === 'web' ? 'web' : null
    })
}

export function initCallFlowLog(): void {
  if (initialised) return
  initialised = true
  primeAppBuild()

  void primeDeviceId()
    .then((id) => {
      deviceId = id
    })
    .catch(() => {})
  // Wait for native Preferences token storage — priming fetchAuthSession too
  // early races migration and can observe an empty JWT store on Android.
  void whenAuthStorageReady()
    .then(() => primeCallTelemetryAuth())
    .catch(() => {})

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushOnUnload()
    })
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', flushOnUnload)
    // On native, an app going to background can suspend the WebView with the
    // queue unsent — flush eagerly there too.
    window.addEventListener('beforeunload', flushOnUnload)
  }

  // Wire the native → JS bridge. Imported lazily to avoid a static cycle and to
  // keep this module usable on web (where the listener is a no-op).
  void import('../services/voice-call/native-call')
    .then(({ addNativeCallTelemetryListener }) => {
      if (typeof addNativeCallTelemetryListener !== 'function') return
      // Lifetime listener on a startup singleton — no teardown path needed.
      void addNativeCallTelemetryListener(ingestNativeTelemetry)
    })
    .catch(() => {})
}
